import React from "react";
import { Drawer } from "vaul";
import type { StockNewsItemData } from "@crypto-trading/common/browser";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import type { LivePriceExchangeCode } from "@/hooks/useTokenLivePrice";

interface StockNewsDetailDrawerProps {
  item: StockNewsItemData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: () => void;
}

function formatPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return "Not available";
  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export const StockNewsDetailDrawer: React.FC<StockNewsDetailDrawerProps> = ({
  item,
  open,
  onOpenChange,
  onTrade,
}) => {
  const matched = Boolean(item?.primaryTicker);
  const liveExchange = (item?.alertPriceExchange ||
    "dynamic") as LivePriceExchangeCode;
  const { price: livePrice } = useTokenLivePrice(
    matched ? item?.priceSymbol : null,
    liveExchange,
    open && matched
  );

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[120]" />
        <Drawer.Content className="bg-[#1a1b20] flex flex-col rounded-t-xl fixed bottom-0 left-0 right-0 max-h-[85vh] z-[121]">
          <div className="px-4 border-b border-sky-500/30 pb-3 pt-2">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-300">
              {item?.sourceName || item?.source || "Stock news"}
            </p>
            <h2 className="mt-1 text-lg font-bold text-text-primary">
              {item?.title}
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {matched && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-text-secondary">Alert price</p>
                  <p className="text-sm text-text-primary">
                    {formatPrice(item?.alertPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">Current price</p>
                  <p className="text-sm text-text-primary">
                    {formatPrice(livePrice)}
                  </p>
                </div>
              </div>
            )}
            <p className="text-sm leading-relaxed text-text-secondary whitespace-pre-wrap">
              {item?.body}
            </p>
            {item?.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-sm text-sky-300 underline"
              >
                Open article
              </a>
            )}
            {matched && onTrade && (
              <button
                type="button"
                onClick={onTrade}
                className="w-full rounded-lg bg-accent py-3 text-sm font-semibold text-white"
              >
                Trade {item?.primaryTicker}
              </button>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
